import { useQueryClient } from "@tanstack/react-query"
import { useRouter } from "@tanstack/react-router"
import { createColumnHelper, flexRender, useTable } from "@tanstack/react-table"
import axios from "axios"
import { Badge } from "design-system/atoms/Badge"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import DateDisplay from "design-system/molecules/DateDisplay"
import { FlagsBadge } from "design-system/molecules/FlagsBadge"
import {
  Table,
  TableBody,
  TableCell,
  TableEmptySlate,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from "design-system/molecules/Table"
import { cn } from "design-system/utils"
import { features, type Features } from "lib/table"
import { setAuthToken } from "modules/auth/lib/setAuthToken"
import { formatExperimentLatency } from "modules/backoffice/lib/experiments/formatExperimentLatency"
import { getExperimentBadgeClassName } from "modules/backoffice/lib/experiments/getExperimentBadgeClassName"
import { formatKnowledgeRate } from "modules/backoffice/lib/knowledge/formatKnowledgeRate"
import { ChannelIcon } from "modules/shared/components/Miscelanea/ChannelIcon"
import { Pulse } from "modules/shared/components/Miscelanea/Pulse"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import {
  BackofficeConversationExperimentsResponseDataSchema,
  BackofficeImpersonateOrganizationResponseDataSchema,
  DataResponse,
} from "schema"
import { z } from "zod"

type Row = z.infer<typeof BackofficeConversationExperimentsResponseDataSchema>[number]

const columnHelper = createColumnHelper<Features, Row>()

type Props = {
  data: z.infer<typeof BackofficeConversationExperimentsResponseDataSchema> | undefined
  isPending: boolean
}

export function BackofficeConversationExperimentsTable({ data, isPending }: Props) {
  const { t } = useTranslation()
  const { buildLocation } = useRouter()
  const queryClient = useQueryClient()

  const columns = useMemo(
    () =>
      columnHelper.columns([
        columnHelper.display({
          id: "company",
          header: t("backoffice.conversationExperiments.table.company"),
          cell: ({ row }) => {
            const conversationIsActive = !row.original.closedAt
            const { name, logo } = row.original.company

            const pulseOrEmpty = conversationIsActive ? (
              <Tooltip>
                <TooltipTrigger>
                  <Pulse className={cn("fill-chart-1")} />
                </TooltipTrigger>
                <TooltipContent side="right">
                  {t("conversations.active.explanation")}
                </TooltipContent>
              </Tooltip>
            ) : null

            return (
              <div className="flex flex-row items-center justify-start gap-4">
                <div className="relative flex w-4 items-center justify-center">{pulseOrEmpty}</div>
                <div className="flex items-center gap-2">
                  <img src={logo ?? ""} alt="" style={{ height: 30 }} />
                  <span>{name}</span>
                </div>
              </div>
            )
          },
          meta: { colSpan: 2 },
        }),
        columnHelper.accessor("channel", {
          header: t("conversations.table.channel"),
          cell: (props) => {
            const channel = props.getValue()
            return <ChannelIcon channel={channel} withTooltip />
          },
          meta: { colSpan: 1, align: "center" as const },
        }),
        columnHelper.accessor("campaignName", {
          header: t("conversations.table.campaign"),
          cell: ({ getValue }) => {
            const campaignName = getValue()
            if (campaignName === null) {
              return (
                <span className="text-muted-foreground font-italic">
                  {t("conversations.table.noCampaign")}
                </span>
              )
            }
            if (!campaignName) {
              return <span className="text-muted-foreground">{t("campaigns.untitled")}</span>
            }
            return campaignName
          },
        }),
        columnHelper.accessor("experiment", {
          header: t("backoffice.conversationExperiments.table.experiment"),
          cell: ({ getValue }) => {
            const name = getValue()
            return (
              <div
                className={cn(
                  "inline-flex w-fit max-w-full items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold shadow-xs",
                  "font-mono normal-case",
                  getExperimentBadgeClassName(name),
                )}
              >
                <span className="block max-w-[min(100%,16rem)] truncate">{name}</span>
              </div>
            )
          },
          meta: { align: "center" as const },
        }),
        columnHelper.accessor("assistantMessages", {
          header: t("backoffice.conversationExperiments.table.assistantMessages"),
          cell: ({ getValue }) => getValue(),
          meta: { align: "center" as const },
        }),
        columnHelper.accessor("questionCount", {
          header: t("backoffice.conversationExperiments.table.questions"),
          cell: ({ getValue }) => getValue(),
          meta: { align: "center" as const },
        }),
        columnHelper.accessor("answeredRate", {
          header: t("backoffice.conversationExperiments.table.answeredRate"),
          cell: ({ getValue }) => formatKnowledgeRate(getValue()),
          meta: { align: "center" as const },
        }),
        columnHelper.accessor("avgLatencySeconds", {
          header: t("backoffice.conversationExperiments.table.avgLatency"),
          cell: ({ getValue }) => formatExperimentLatency(getValue()),
          meta: { align: "center" as const },
        }),
        columnHelper.accessor("hasTicket", {
          header: t("backoffice.conversationExperiments.table.hasTicket"),
          cell: ({ getValue }) => {
            const hasTicket = getValue()
            return (
              <Badge variant={hasTicket ? "warning" : "secondary"}>
                {hasTicket
                  ? t("backoffice.conversationExperiments.table.ticketYes")
                  : t("backoffice.conversationExperiments.table.ticketNo")}
              </Badge>
            )
          },
          meta: { align: "center" as const },
        }),
        columnHelper.display({
          id: "flags",
          header: t("backoffice.flags.table.flags"),
          cell: ({ row }) => {
            const { issueCount, frictionCount } = row.original
            if (issueCount <= 0 && frictionCount <= 0) return null
            return <FlagsBadge issuesCount={issueCount} frictionsCount={frictionCount} />
          },
          meta: { align: "center" as const },
        }),
        columnHelper.accessor("createdAt", {
          header: t("backoffice.conversationExperiments.table.createdAt"),
          cell: ({ getValue }) => <DateDisplay date={getValue()} />,
        }),
      ]),
    [t],
  )

  const table = useTable({
    data: data ?? [],
    columns,
    features,
  })

  async function handleRowClick(row: Row) {
    const link = buildLocation({
      to: "/conversations/list/$customerId",
      params: { customerId: row.customerId },
      search: { tab: "conversations", conversationId: Number(row.conversationId) },
    }).href

    try {
      await navigator.clipboard.writeText([window.location.origin, link].join(""))
    } catch (_error) {
      // Ignore clipboard errors, still attempt impersonation and navigation
    }

    try {
      type ImpersonateResponse = DataResponse<
        z.infer<typeof BackofficeImpersonateOrganizationResponseDataSchema>
      >

      const response = await axios.get<ImpersonateResponse>(
        `/backoffice/organizations/impersonate/${row.organizationId}`,
      )

      const tokenPayload = response.data.data
      setAuthToken({ queryClient, token: tokenPayload.token })

      window.open([window.location.origin, link].join(""), "_blank")
    } catch (error) {
      console.error("Failed to impersonate organization from conversation experiments table", {
        error,
      })
    }
  }

  function renderContent() {
    if (isPending || data === undefined) {
      return <TableSkeleton colSpans={columns.map((c) => c.meta?.colSpan ?? 1)} />
    }

    if (data.length === 0) {
      return (
        <TableEmptySlate
          colSpan={columns.reduce((acc, c) => acc + (c.meta?.colSpan ?? 1), 0)}
          message={t("backoffice.conversationExperiments.table.empty")}
        />
      )
    }

    return table.getRowModel().rows.map((row) => (
      <TableRow
        key={row.id}
        onClick={() => handleRowClick(row.original)}
        className="cursor-pointer"
      >
        {row.getVisibleCells().map((cell) => (
          <TableCell key={cell.id} {...cell.column.columnDef.meta}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
    ))
  }

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHead key={header.id} {...header.column.columnDef.meta}>
                {flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>{renderContent()}</TableBody>
    </Table>
  )
}
